import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// 契约（与 index.html 预水合脚本一致）：localStorage 存在 'theme' 键
// 当且仅当用户显式选择过主题；未选择时始终跟随系统偏好。
function getInitialTheme(): Theme {
  if (typeof document !== 'undefined' && document.documentElement.classList.contains('dark')) {
    return 'dark';
  }
  return 'light';
}

// 一次性迁移：旧实现会在未显式选择时也写入 'theme'（配套 'theme-explicit' 标记），
// 把这类误写清理掉以恢复跟随系统。必须用 marker 保证只跑一次——
// 新契约下显式选择也只写 'theme'，重复执行会把用户的真实选择误删。
const MIGRATION_KEY = 'theme-migrated-v1';

function migrateLegacyKeys() {
  if (localStorage.getItem(MIGRATION_KEY)) return;
  if (localStorage.getItem('theme') && !localStorage.getItem('theme-explicit')) {
    localStorage.removeItem('theme');
  }
  localStorage.removeItem('theme-explicit');
  localStorage.setItem(MIGRATION_KEY, '1');
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    migrateLegacyKeys();
    return getInitialTheme();
  });

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
  }, [theme]);

  // 未显式选择时跟随系统偏好变化
  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      if (!localStorage.getItem('theme')) {
        setThemeState(e.matches ? 'dark' : 'light');
      }
    };
    media.addEventListener('change', handler);
    return () => media.removeEventListener('change', handler);
  }, []);

  const setTheme = useCallback((next: Theme) => {
    localStorage.setItem('theme', next);
    setThemeState(next);
  }, []);

  const value = useMemo(() => ({ theme, setTheme }), [theme, setTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
